import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    GridColDef,
    GridRenderCellParams,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { useTranslation } from "react-i18next";
import { Base } from "../../../framework/base";
import { FormTemplateDto } from "../../../models/formTemplate/formTemplateDto";
import * as formTemplateService from "../../../services/formTemplateService";
import { useFetch } from "../../../hooks/useFetch";
import { useReload } from "../../../hooks/useReload";
import { MuiDataGrid } from "../../framework/muiDataGrid";
import { LoadingIndicator } from "../../framework/loadingIndicatorNew";
import { useAppDispatch } from "../../../framework/customStore";
import {
    setConfirmation,
    showSuccessMessage,
} from "../../../models/store/storeActions";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";
import { handleApiError } from "../../../models/store/storeEffects";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import { MuiSwitch } from "../../framework/muiSwitch";
import useEmployeeUiState from "../../../hooks/useEmployeeUiState";
import { FormSchemaDialog } from "./formSchemaDialog";
import { FormEditorContent } from "../formEditorView/formEditorView";

const GRID_STATE_KEY = "formTemplatesGrid";

export const FormsViewContent = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [formTemplates, setFormTemplates] = useState<
        FormTemplateDto[] | null
    >(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
        null
    );
    const [editorMode, setEditorMode] = useState<{
        templateId?: string;
    } | null>(null);
    const [showInactive, setShowInactive] = useState(false);
    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(
        null
    );
    const [reloadKey, reload] = useReload();
    const apiRef = useGridApiRef();

    const {
        value: persistedGridState,
        setValue: setPersistedGridState,
        initialized: gridStateInitialized,
    } = useEmployeeUiState<GridInitialStatePremium>(GRID_STATE_KEY, {});

    const persistGridState = useCallback(() => {
        if (apiRef?.current?.exportState) {
            const currentState = apiRef.current.exportState();
            setPersistedGridState({
                sorting: currentState.sorting,
                columns: {
                    orderedFields: currentState.columns?.orderedFields,
                },
            });
        }
    }, [apiRef, setPersistedGridState]);

    useEffect(() => {
        if (!highlightedRowId) return;
        const timer = setTimeout(() => setHighlightedRowId(null), 3000);
        return () => clearTimeout(timer);
    }, [highlightedRowId]);

    const fetchTemplates = useCallback(
        (signal?: AbortSignal) =>
            formTemplateService.getFormTemplates(
                showInactive ? undefined : true,
                signal
            ),
        [showInactive]
    );

    useFetch(
        fetchTemplates,
        {
            onSuccess: (response) => setFormTemplates(response.data),
            withLoadingIndicator: false,
        },
        [reloadKey, showInactive]
    );

    const handleDelete = useCallback(
        (id: string, name: string) => {
            dispatch(
                setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("settings.forms.confirmDelete", { name }),
                    null,
                    () => {
                        formTemplateService
                            .deleteFormTemplate(id)
                            .then(() => {
                                dispatch(
                                    showSuccessMessage(
                                        t("settings.forms.deleteSuccess")
                                    )
                                );
                                reload();
                            })
                            .catch((e) => handleApiError(e, dispatch));
                    }
                )
            );
        },
        [dispatch, t, reload]
    );

    const handleToggleActive = useCallback(
        (original: FormTemplateDto, newValue: boolean) => {
            setFormTemplates(
                (prev) =>
                    prev?.map((t) =>
                        t.id === original.id ? { ...t, isActive: newValue } : t
                    ) ?? null
            );
            formTemplateService
                .saveFormTemplate({
                    id: original.id,
                    rowId: original.rowId,
                    name: original.name,
                    jsonSchema: JSON.stringify(original.jsonSchema),
                    targetObjectType: original.targetObjectType,
                    isActive: newValue,
                })
                .then(() => reload())
                .catch((e) => {
                    setFormTemplates(
                        (prev) =>
                            prev?.map((t) =>
                                t.id === original.id
                                    ? { ...t, isActive: !newValue }
                                    : t
                            ) ?? null
                    );
                    handleApiError(e, dispatch);
                });
        },
        [dispatch, reload]
    );

    const handleDuplicate = useCallback(
        (id: string) => {
            formTemplateService
                .getFormTemplate(id)
                .then((response) => {
                    const original = response.data;
                    return formTemplateService.saveFormTemplate({
                        name: `${original.name} (${t("settings.forms.copy")})`,
                        jsonSchema: JSON.stringify(original.jsonSchema),
                        targetObjectType: original.targetObjectType,
                        isActive: false,
                    });
                })
                .then((response) => {
                    dispatch(
                        showSuccessMessage(t("settings.forms.duplicateSuccess"))
                    );
                    setHighlightedRowId(response.data.id);
                    setShowInactive(true);
                    reload();
                })
                .catch((e) => handleApiError(e, dispatch));
        },
        [dispatch, t, reload]
    );

    const columns: GridColDef<FormTemplateDto>[] = useMemo(
        () => [
            {
                field: "name",
                headerName: t("settings.forms.formTemplate"),
                flex: 2,
            },
            {
                field: "targetObjectType",
                headerName: t("settings.forms.targetObjectType"),
                flex: 1,
                renderCell: ({
                    value,
                }: GridRenderCellParams<FormTemplateDto, string | null>) =>
                    value
                        ? t(`settings.formEditor.targetObjectType${value}`)
                        : "-",
            },
            {
                field: "isActive",
                headerName: t("common.inUse"),
                width: 100,
                type: "boolean",
                renderCell: (
                    params: GridRenderCellParams<FormTemplateDto, boolean>
                ) => (
                    <Switch
                        size="small"
                        color="success"
                        checked={!!params.value}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(_, checked) =>
                            handleToggleActive(params.row, checked)
                        }
                    />
                ),
            },
            {
                field: "modifiedAt",
                headerName: t("settings.forms.modifiedAt"),
                flex: 1,
                renderCell: ({
                    value,
                }: GridRenderCellParams<FormTemplateDto, string | null>) =>
                    value ? Base.dayjsDateToDateTimeStr(value) : "-",
            },
            {
                field: "actions",
                headerName: "",
                type: "actions",
                width: 180,
                renderCell: (params: GridRenderCellParams<FormTemplateDto>) => (
                    <>
                        <Tooltip title={t("settings.forms.showForm")}>
                            <IconButton
                                size="small"
                                color="primary"
                                data-testid={`forms-show-${params.row.id}`}
                                onClick={() =>
                                    setSelectedTemplateId(params.row.id)
                                }
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.edit")}>
                            <IconButton
                                size="small"
                                color="primary"
                                data-testid={`forms-edit-${params.row.id}`}
                                onClick={() =>
                                    setEditorMode({
                                        templateId: params.row.id,
                                    })
                                }
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("settings.forms.duplicate")}>
                            <IconButton
                                size="small"
                                color="primary"
                                data-testid={`forms-duplicate-${params.row.id}`}
                                onClick={() => handleDuplicate(params.row.id)}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.remove")}>
                            <IconButton
                                size="small"
                                color="error"
                                data-testid={`forms-delete-${params.row.id}`}
                                onClick={() =>
                                    handleDelete(params.row.id, params.row.name)
                                }
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                ),
            },
        ],
        [t, handleToggleActive, handleDuplicate, handleDelete]
    );

    if (editorMode) {
        return (
            <FormEditorContent
                templateId={editorMode.templateId}
                onCancel={() => {
                    setEditorMode(null);
                    reload();
                }}
            />
        );
    }

    if (!formTemplates || !gridStateInitialized) return <LoadingIndicator />;

    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    width: "100%",
                    marginBottom: 8,
                }}
            >
                <MuiSwitch
                    id="settings-forms-showInactive"
                    label={t("settings.forms.showInactive")}
                    checked={showInactive}
                    onChange={(_, checked) => setShowInactive(checked)}
                />
            </div>
            <MuiDataGrid
                apiRef={apiRef}
                title={t("settings.forms.tabTitle")}
                columns={columns}
                rows={formTemplates}
                getRowId={(row: FormTemplateDto) => row.id}
                density="compact"
                disableRowSelectionOnClick
                rowSelectionModel={highlightedRowId ? [highlightedRowId] : []}
                onRowSelectionModelChange={() => setHighlightedRowId(null)}
                onRowDoubleClick={(params) =>
                    setEditorMode({ templateId: params.row.id })
                }
                onSortModelChange={persistGridState}
                onColumnOrderChange={persistGridState}
                initialState={{
                    sorting: persistedGridState?.current?.sorting ?? {
                        sortModel: [{ field: "name", sort: "asc" }],
                    },
                    columns: persistedGridState?.current?.columns,
                }}
                slots={{
                    noRowsOverlay: () => (
                        <Alert severity="info" sx={{ p: 2 }}>
                            {t("settings.forms.noTemplates")}
                        </Alert>
                    ),
                }}
                customToolbarButtons={[
                    {
                        key: "add",
                        startIcon: <AddIcon />,
                        color: "success",
                        variant: "contained",
                        children: t("common.addNew"),
                        onClick: () => setEditorMode({}),
                        "data-testid": "forms-add-new",
                    },
                ]}
            />
            {selectedTemplateId && (
                <FormSchemaDialog
                    templateId={selectedTemplateId}
                    onClose={() => setSelectedTemplateId(null)}
                />
            )}
        </>
    );
};
